import styles from './SummarySparkline.scss';

const SPARKLINE_WIDTH = 100;
const SPARKLINE_HEIGHT = 40;
const SPARKLINE_VERTICAL_PADDING = 5;

interface SparklinePoint {
  x: number;
  y: number;
}

export interface SummaryCardConfig {
  label: string;
  value?: number;
  context: string;
  delta?: string;
  sparklineValues?: number[];
  showSparklineShadow?: boolean;
}

const buildLinePath = (points: SparklinePoint[]): string => {
  if (points.length === 0) {
    return '';
  }

  const [first, ...rest] = points;

  return rest.reduce(
    (path, point) => `${path} L ${point.x} ${point.y}`,
    `M ${first.x} ${first.y}`,
  );
};

export const SummarySparkline = ({
  values,
  showShadow = false,
}: {
  values: number[];
  showShadow?: boolean;
}) => {
  if (!values.length) {
    return <div className={styles.sparklineEmpty} />;
  }

  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);
  const range = maxValue - minValue || 1;
  const usableHeight = SPARKLINE_HEIGHT - SPARKLINE_VERTICAL_PADDING * 2;

  const points: SparklinePoint[] = values.map((value, index) => ({
    x: (index / Math.max(values.length - 1, 1)) * SPARKLINE_WIDTH,
    y:
      SPARKLINE_VERTICAL_PADDING +
      usableHeight -
      ((value - minValue) / range) * usableHeight,
  }));

  const linePath = buildLinePath(points);
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${SPARKLINE_HEIGHT} L ${points[0].x} ${SPARKLINE_HEIGHT} Z`;

  return (
    <svg
      aria-hidden
      className={styles.sparkline}
      preserveAspectRatio="none"
      viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
    >
      {showShadow && (
        <path className={styles.sparklineShadow} d={areaPath} stroke="none" />
      )}
      <path
        d={linePath}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};
